import { useState } from "react";

// All pages — exported so FinanceDashboard can derive page titles
export const NAV_ITEMS = [
  { id: "dashboard",    label: "Overview"     },
  { id: "accounts",     label: "Accounts"     },
  { id: "transactions", label: "Transactions" },
  { id: "budget",       label: "Budget"       },
  { id: "investments",  label: "Investments"  },
  { id: "calculators",  label: "Calculators"  },
  { id: "blog",         label: "Blog"         },
  { id: "support",      label: "Support"      },
  { id: "admin",        label: "Admin"        },
];

// Overview is the parent; all others are nested children
const PARENT   = { id: "dashboard",    label: "Overview",     icon: "◈" };
const CHILDREN = [
  { id: "accounts",     label: "Accounts",     icon: "◉" },
  { id: "transactions", label: "Transactions", icon: "⊞" },
  { id: "budget",       label: "Budget",       icon: "◎" },
  { id: "investments",  label: "Investments",  icon: "△" },
];

// Top-level standalone items (not nested under Overview)
const STANDALONE = [
  { id: "calculators", label: "Calculators", icon: "∑" },
  { id: "blog",        label: "Blog",        icon: "✦" },
  { id: "support",     label: "Support",     icon: "◎" },
];

export default function Sidebar({ activeNav, onNavChange, isAdmin, planState, mobileOpen, onMobileClose }) {
  const [expanded, setExpanded] = useState(true);

  // Is a child page currently active?
  const childActive = CHILDREN.some(c => c.id === activeNav);
  const parentActive = activeNav === PARENT.id;

  const handleParentClick = () => {
    if (parentActive) {
      // Already on Overview → just toggle collapse
      setExpanded(p => !p);
    } else {
      // Navigate to Overview and expand
      onNavChange(PARENT.id);
      setExpanded(true);
      if (onMobileClose) onMobileClose();
    }
  };

  const handleChildClick = (id) => {
    onNavChange(id);
    setExpanded(true); // ensure group stays open when child is picked
    if (onMobileClose) onMobileClose();
  };

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .sidebar-aside {
            position: fixed !important;
            top: 0; left: 0; bottom: 0;
            z-index: 300;
            transform: translateX(-100%);
            transition: transform 0.25s ease !important;
            box-shadow: 4px 0 24px rgba(0,0,0,0.6);
          }
          .sidebar-aside.mobile-open {
            transform: translateX(0) !important;
          }
        }
        @media (min-width: 769px) {
          .sidebar-backdrop { display: none !important; }
        }
      `}</style>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="sidebar-backdrop"
          onClick={onMobileClose}
          style={{
            position: "fixed", inset: 0, zIndex: 299,
            background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
          }}
        />
      )}
    <aside className={`sidebar-aside${mobileOpen ? " mobile-open" : ""}`} style={{
      width: "220px", flexShrink: 0, background: "var(--bg-deepest)",
      borderRight: "1px solid var(--border)",
      display: "flex", flexDirection: "column", overflow: "hidden",
      transition: "transform 0.25s ease",
    }}>
      {/* ── Logo ──────────────────────────────────────────────── */}
      <div style={{ padding: "28px 24px 24px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            width: "32px", height: "32px", borderRadius: "10px",
            background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px",
          }}>◈</div>
          <div>
            <div style={{ fontSize: "17px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "800", letterSpacing: "-0.01em", color: "var(--text-primary)" }}>Ledger</div>
            <div style={{ fontSize: "9px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.18em", textTransform: "uppercase" }}>Personal Finance</div>
          </div>
        </div>
      </div>

      {/* ── Navigation ────────────────────────────────────────── */}
      <nav style={{ padding: "16px 12px", flex: 1 }}>

        {/* ── Overview (parent row) ─────────────────────────── */}
        <button
          onClick={handleParentClick}
          style={{
            width: "100%", display: "flex", alignItems: "center",
            padding: "11px 12px 11px 14px", borderRadius: "10px", border: "none",
            background: parentActive
              ? "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.06))"
              : childActive
              ? "rgba(212,175,55,0.04)"
              : "transparent",
            color: parentActive ? "#D4AF37" : childActive ? "var(--text-secondary)" : "var(--text-muted)",
            cursor: "pointer", fontSize: "13px", fontFamily: "'Capsule Sans', sans-serif",
            fontWeight: parentActive || childActive ? "600" : "400",
            transition: "all 0.2s", textAlign: "left", marginBottom: "2px",
            borderLeft: parentActive ? "2px solid #D4AF37" : childActive ? "2px solid var(--border-active)" : "2px solid transparent",
          }}
          onMouseEnter={e => { if (!parentActive) e.currentTarget.style.color = "var(--text-primary)"; }}
          onMouseLeave={e => { if (!parentActive) e.currentTarget.style.color = childActive ? "var(--text-secondary)" : "var(--text-muted)"; }}
        >
          <span style={{ fontSize: "15px", width: "18px", textAlign: "center", marginRight: "10px" }}>{PARENT.icon}</span>
          <span style={{ flex: 1 }}>{PARENT.label}</span>
          {/* Chevron */}
          <span style={{
            fontSize: "10px",
            color: parentActive ? "#D4AF37" : "#243424",
            transition: "transform 0.22s ease",
            display: "inline-block",
            transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
            lineHeight: 1,
          }}>▾</span>
        </button>

        {/* ── Children (collapsible group) ─────────────────── */}
        <div style={{
          overflow: "hidden",
          maxHeight: expanded ? `${CHILDREN.length * 56}px` : "0px",
          transition: "max-height 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
        }}>
          {/* Left tree track */}
          <div style={{ position: "relative", marginLeft: "22px" }}>
            {/* Vertical guide line */}
            <div style={{
              position: "absolute", left: "8px", top: "6px",
              width: "1px",
              bottom: "18px",
              background: "var(--border-strong)",
              borderRadius: "1px",
            }} />

            {CHILDREN.map((child, idx) => {
              const isActive = activeNav === child.id;
              const isLast   = idx === CHILDREN.length - 1;
              return (
                <div key={child.id} style={{ position: "relative", paddingLeft: "24px", marginBottom: isLast ? "4px" : "1px" }}>
                  {/* Horizontal connector tick */}
                  <div style={{
                    position: "absolute", left: "8px",
                    top: "50%", transform: "translateY(-50%)",
                    width: "10px", height: "1px",
                    background: isActive ? "#D4AF37" : "var(--border-strong)",
                    transition: "background 0.2s",
                  }} />
                  {/* Dot on connector */}
                  <div style={{
                    position: "absolute", left: "14px",
                    top: "50%", transform: "translateY(-50%)",
                    width: "4px", height: "4px", borderRadius: "50%",
                    background: isActive ? "#D4AF37" : "var(--border-strong)",
                    transition: "background 0.2s",
                  }} />

                  <button
                    onClick={() => handleChildClick(child.id)}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: "8px",
                      padding: "9px 12px", borderRadius: "8px", border: "none",
                      background: isActive
                        ? "linear-gradient(135deg, rgba(212,175,55,0.14), rgba(212,175,55,0.04))"
                        : "transparent",
                      color: isActive ? "#D4AF37" : "var(--text-muted)",
                      cursor: "pointer", fontSize: "13px",
                      fontFamily: "'Capsule Sans', sans-serif",
                      fontWeight: isActive ? "600" : "400",
                      transition: "all 0.18s", textAlign: "left",
                      borderLeft: isActive ? "2px solid #D4AF37" : "2px solid transparent",
                    }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.background = "var(--bg-hover)"; } }}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.background = "transparent"; } }}
                  >
                    <span style={{ fontSize: "13px", width: "16px", textAlign: "center", opacity: isActive ? 1 : 0.7 }}>{child.icon}</span>
                    {child.label}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </nav>

      {/* ── Standalone items ──────────────────────────────── */}
      <div style={{ marginTop: "8px", borderTop: "1px solid var(--border)", paddingTop: "8px" }}>
        {[...STANDALONE, ...(isAdmin ? [{ id: "admin", label: "Admin", icon: "⚙" }] : [])].map(item => {
          const isActive = activeNav === item.id;
          return (
            <button
              key={item.id}
              onClick={() => { onNavChange(item.id); if (onMobileClose) onMobileClose(); }}
              style={{
                width: "100%", display: "flex", alignItems: "center",
                padding: "11px 12px 11px 14px", borderRadius: "10px", border: "none",
                background: isActive
                  ? "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.06))"
                  : "transparent",
                color: isActive ? "#D4AF37" : item.id === "admin" ? "#D4AF3788" : "var(--text-muted)",
                cursor: "pointer", fontSize: "13px", fontFamily: "'Capsule Sans', sans-serif",
                fontWeight: isActive ? "600" : "400",
                transition: "all 0.2s", textAlign: "left", marginBottom: "2px",
                borderLeft: isActive ? "2px solid #D4AF37" : "2px solid transparent",
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = "var(--text-primary)"; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = item.id === "admin" ? "#D4AF3788" : "var(--text-muted)"; }}
            >
              <span style={{ fontSize: "15px", width: "18px", textAlign: "center", marginRight: "10px" }}>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.id === "admin" && <span style={{ fontSize: "9px", padding: "1px 6px", borderRadius: "4px", background: "rgba(212,175,55,0.15)", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace" }}>ADMIN</span>}
            </button>
          );
        })}
      </div>

      {/* ── Sync Status + Subscription ────────────────────── */}
      <div style={{ padding: "16px 16px 24px", borderTop: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#52D68A", animation: "pulse-dot 2s infinite" }} />
          <span style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>Synced just now</span>
        </div>

        {/* ── Premium ── */}
        {planState?.plan === "premium" && (
          <div style={{ background: "rgba(212,175,55,0.07)", borderRadius: "10px", padding: "12px", border: "1px solid rgba(212,175,55,0.2)" }}>
            <div style={{ fontSize: "10px", color: "#D4AF37", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "4px" }}>SUBSCRIPTION</div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#D4AF37" }}>Premium Plan</div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>$9.99 / month</div>
          </div>
        )}

        {/* ── Trial ── */}
        {planState?.plan === "trial" && (
          <div style={{ background: "var(--bg-card)", borderRadius: "10px", padding: "12px", border: "1px solid var(--border-strong)" }}>
            <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "4px" }}>SUBSCRIPTION</div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)" }}>Free Trial</div>
            <div style={{ fontSize: "11px", color: planState.daysLeft <= 3 ? "#e07070" : "#aaa", marginTop: "2px" }}>
              {planState.daysLeft > 0 ? `${planState.daysLeft} day${planState.daysLeft !== 1 ? "s" : ""} remaining` : "Trial ended"}
            </div>
            {/* Progress bar */}
            <div style={{ marginTop: "8px", background: "var(--bg-main)", borderRadius: "4px", height: "3px", overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: "4px",
                width: `${Math.round(((planState.daysUsed || 0) / (planState.totalDays || 14)) * 100)}%`,
                background: planState.daysLeft <= 3 ? "#e07070" : "linear-gradient(90deg, #D4AF37, #9A7C1E)",
                transition: "width 0.4s ease",
              }} />
            </div>
          </div>
        )}

        {/* ── Expired / canceled ── */}
        {planState?.plan === "expired" && (
          <div style={{ background: "var(--bg-surface)", borderRadius: "10px", padding: "12px", border: "1px solid var(--border-strong)" }}>
            <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "4px" }}>SUBSCRIPTION</div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#e07070" }}>Expired</div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>Renew to regain access</div>
          </div>
        )}

        {/* ── Loading ── */}
        {(!planState || planState.plan === "loading") && (
          <div style={{ background: "var(--bg-card)", borderRadius: "10px", padding: "12px", border: "1px solid var(--border-strong)" }}>
            <div style={{ fontSize: "10px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", marginBottom: "4px" }}>SUBSCRIPTION</div>
            <div style={{ fontSize: "13px", color: "var(--text-faint)" }}>—</div>
          </div>
        )}
      </div>
    </aside>
    </>
  );
}
